import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { DataTable } from "primereact/datatable";
import { useAPIGetCustomerDiscount } from "../api/api-get-customer-discount";
import { Column } from "primereact/column";
import CustomerDiscountFilter from "./customer-discount-filter";
import {
  genTemplateBodyNumber,
  genTemplateBodyText,
} from "../../../../components/utils/TableTemplateBodyGenerator";
import { Paginator } from "primereact/paginator";
import { Button } from "primereact/button";
import { format } from "date-fns";
import useIsMobile from "../../../../hooks/useIsMobile";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { useSelector } from "react-redux";

const CustomerDiscountList = forwardRef((props, ref) => {
  const { onClickRowDelete, onClickRowEdit, onClickAddNew } = props;
  const isMobile = useIsMobile();
  const employee = useSelector((state) => state.auth.employee);

  const {
    loading,
    loadingMain,
    loadingPending,
    loadingExpired,
    listData,
    listDataPending,
    listDataExpired,
    fetchListData,
    filterValue,
    setFilterValue,
    handleOnClickRefresh,
    handleOnPage,
    paging,
    handleOnUpdateViewRow,
    handleOnDeleteViewRow,
    handleVerifBC,
    handleVerifOwner,
  } = useAPIGetCustomerDiscount();

  useImperativeHandle(ref, () => ({
    handleOnUpdateViewRow: (row) => {
      handleOnUpdateViewRow(row);
    },
    handleOnDeleteViewRow: (row) => {
      handleOnDeleteViewRow(row);
    },
  }));

  // use effect section

  useEffect(() => {
    fetchListData({ page: paging.page, take: paging.take });
  }, []);

  // Verification functions
  const confirmVerifBC = (row) => {
    confirmDialog({
      message: `Are you sure you want to verify BC for ${row.data_customer?.name}?`,
      header: 'BC Verification Confirmation',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-info',
      accept: () => {
        handleVerifBC(row.id, () => {
          fetchListData({ page: paging.page, take: paging.take });
        });
      },
    });
  };

  const confirmVerifFinance = (row) => {
    confirmDialog({
      message: `Are you sure you want to verify Finance for ${row.data_customer?.name}?`,
      header: 'Finance Verification Confirmation',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-success',
      accept: () => {
        handleVerifOwner(row.id, () => {
          fetchListData({ page: paging.page, take: paging.take });
        });
      },
    });
  };

  //  custom ui section

  // PART_TEMPLATE_BODY_GENERATOR
  const CustomerBodyTemplate = (row) => {
    const { data_customer, data_sales_discount } = row;

    return <div className="col-start-start gap-1">
      <span>{data_customer?.name} ({data_customer?.code})</span>
      <span className="text-sm font-semibold">BC: {data_customer?.data_bc?.name}</span>
      {row?.sales_discount_id && (
        <span className="text-sm">Invoice: {data_sales_discount?.data_sales?.number}</span>
      )}
    </div>
  };
  const DiscountBodyTemplate = (row) => {
    const { expired_date, value, type } = row;

    return <div className="col-start-start gap-1">
      <span>{row?.name}</span>
      <span className="text-sm">
        Value:{" "}
        {type === 1
          ? new Intl.NumberFormat("id-ID", {
            style: "currency",
            currency: "IDR",
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }).format(value)
          : `${value}%`}
      </span>
      <span className="text-sm font-semibold">Expired: {format(new Date(expired_date), "dd MMMM yyyy")}</span>
      {row.description && <span className="text-sm text-gray-600">Description: {row.description}</span>}
    </div>
  };

  const TimelineBodyTemplate = (row) => {
    const { data_created_by, created_by_datetime, data_verif_bc, verif_bc_datetime, data_verif_owner, verif_owner_datetime } = row;

    const today = new Date();
    const isBC = employee?.user_type === 3;
    const isFinance = employee?.user_type === 11;
    const isCompleted = data_verif_owner && verif_owner_datetime;
    const canVerifyBC = isBC && !row.verif_bc_id && !isCompleted && today < new Date(row.expired_date);
    const canVerifyFinance = isFinance && row.verif_bc_id && !row.verif_owner_id && today < new Date(row.expired_date);
    const expired = today > new Date(row.expired_date);

    return (
      <div className="col-start-start gap-2 py-2 w-full">
        {/* Horizontal Timeline */}
        <div className="flex items-start justify-between w-full relative">
          {/* Created Step */}
          <div className="flex flex-col items-center gap-1 flex-1 min-w-0">
            <div className="w-10 h-10 rounded-full bg-green-600 flex items-center justify-center flex-shrink-0">
              <i className="pi pi-user text-white text-sm"></i>
            </div>
            <span className="text-xs font-semibold text-gray-700 text-center">Created</span>
            <span className="text-sm font-semibold text-center break-words w-full px-1">{data_created_by?.name}</span>
            <span className="text-xs font-semibold text-gray-600">{format(new Date(created_by_datetime), "dd/MM HH:mm")}</span>
          </div>

          {/* Connector Line 1 */}
          <div className={`flex-1 h-1 ${data_verif_bc || isCompleted ? 'bg-green-600' : 'bg-gray-300'} self-start mt-5 mx-2`}></div>

          {/* BC Verified Step */}
          <div className="flex flex-col items-center gap-1 flex-1 min-w-0">
            <div className={`w-10 h-10 rounded-full flex-shrink-0 ${data_verif_bc ? 'bg-green-600' : canVerifyBC ? 'bg-blue-600 shadow-lg' : 'bg-gray-300'} flex items-center justify-center ${canVerifyBC && 'cursor-pointer hover:bg-green-700 hover:shadow-xl transition-all'}`}
              onClick={() => canVerifyBC && confirmVerifBC(row)}
            >
              {canVerifyBC ? (
                <i className="pi pi-check text-white text-sm font-bold"></i>
              ) : (
                <i className={`pi pi-check text-white text-sm ${!data_verif_bc && 'opacity-50'}`}></i>
              )}
            </div>
            <span className={`text-xs font-semibold text-center ${data_verif_bc ? 'text-green-600' : canVerifyBC ? 'text-blue-700' : 'text-gray-400'}`}>
              {canVerifyBC ? 'Click to Verify BC' : data_verif_bc ? 'BC Verified' : 'Verify BC'}
            </span>
            {data_verif_bc ? (
              <>
                <span className="text-sm font-semibold text-center break-words w-full px-1">{data_verif_bc?.name}</span>
                <span className="text-xs font-semibold text-gray-600">{format(new Date(verif_bc_datetime), "dd/MM HH:mm")}</span>
              </>
            ) : canVerifyBC ? (
              <span className="text-xs text-blue-700 text-center font-bold">Ready to Verify</span>
            ) : (
              <span className="text-xs text-gray-400 text-center">
                {expired
                  ? "Expired"
                  : data_verif_owner
                    ? "Verified by Finance"
                    : "Pending"}
              </span>
            )}
          </div>

          {/* Connector Line 2 */}
          <div className={`flex-1 h-1 ${data_verif_owner ? 'bg-green-500' : 'bg-gray-300'} self-start mt-5 mx-2`}></div>

          {/* Finance Verified Step */}
          <div className="flex flex-col items-center gap-1 flex-1 min-w-0">
            <div className={`w-10 h-10 rounded-full flex-shrink-0 ${data_verif_owner ? 'bg-green-600' : canVerifyFinance ? 'bg-blue-600 shadow-lg' : 'bg-gray-300'} flex items-center justify-center ${canVerifyFinance && 'cursor-pointer hover:bg-blue-700 hover:shadow-xl transition-all'}`}
              onClick={() => canVerifyFinance && confirmVerifFinance(row)}
            >
              {canVerifyFinance ? (
                <i className="pi pi-check-circle text-white text-sm font-bold"></i>
              ) : (
                <i className={`pi pi-check-circle text-white text-sm ${!data_verif_owner && 'opacity-50'}`}></i>
              )}
            </div>
            <span className={`text-xs font-semibold text-center ${data_verif_owner ? 'text-green-600' : canVerifyFinance ? 'text-blue-700' : 'text-gray-400'}`}>
              {canVerifyFinance ? 'Click to Verify Finance' : data_verif_owner ? 'Finance Verified' : 'Verify Finance'}
            </span>
            {data_verif_owner ? (
              <>
                <span className="text-sm font-semibold text-center break-words w-full px-1">{data_verif_owner?.name}</span>
                <span className="text-xs font-semibold text-gray-600">{format(new Date(verif_owner_datetime), "dd/MM HH:mm")}</span>
              </>
            ) : canVerifyFinance ? (
              <span className="text-xs text-blue-700 text-center font-bold">Ready to Verify</span>
            ) : (
              <span className="text-xs text-gray-400 text-center">
                {expired
                  ? "Expired"
                  : "Pending"
                }
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  const actionBodyTemplate = (row) => {
    const isBC = employee?.user_type === 3;
    const isFinance = employee?.user_type === 11;
    const isCompleted = row.verif_owner_id;
    const canVerifyBC = isBC && !row.verif_bc_id && !isCompleted;
    const canVerifyFinance = isFinance && !row.verif_owner_id;

    return (
      <div className="row-center-center gap-1">
        <Button
          icon="pi pi-pencil"
          rounded
          text
          severity="primary"
          onClick={() => {
            if (onClickRowEdit) onClickRowEdit(row);
          }}
          disabled={row.verif_bc_id || row.verif_owner_id}
          tooltip="Edit"
          tooltipOptions={{ position: 'top' }}
          className="!w-8 !h-8"
        />
        <Button
          icon="pi pi-trash"
          rounded
          text
          severity="danger"
          onClick={() => {
            if (onClickRowDelete) onClickRowDelete(row);
          }}
          tooltip="Delete"
          tooltipOptions={{ position: 'top' }}
          className="!w-8 !h-8"
        />
      </div>
    );
  };

  const getStatusInfo = (row) => {
    const { expired_date, data_verif_bc, verif_bc_datetime, data_verif_owner, verif_owner_datetime } = row;

    const today = new Date();
    const expired = today > new Date(expired_date);

    if (data_verif_owner && verif_owner_datetime) {
      return {
        status: 'Completed',
        color: 'bg-green-500',
        icon: 'pi-check-circle',
        textColor: 'text-green-700',
        bgLight: 'bg-green-50'
      };
    } else if (data_verif_bc && verif_bc_datetime) {
      return {
        status: 'BC Verified',
        color: 'bg-blue-500',
        icon: 'pi-clock',
        textColor: 'text-blue-700',
        bgLight: 'bg-blue-50'
      };
    } else if (expired) {
      return {
        status: 'Expired',
        color: 'bg-red-500',
        icon: 'pi-times-circle',
        textColor: 'text-red-700',
        bgLight: 'bg-red-50'
      };
    } else {
      return {
        status: 'Pending',
        color: 'bg-orange-500',
        icon: 'pi-hourglass',
        textColor: 'text-orange-700',
        bgLight: 'bg-orange-50'
      };
    }
  };

  const MobileCardItem = ({ row }) => {
    const statusInfo = getStatusInfo(row);
    const { data_customer, data_sales_discount, value, type, expired_date, data_created_by, created_by_datetime, data_verif_bc, verif_bc_datetime, data_verif_owner, verif_owner_datetime } = row;

    const today = new Date();
    const isBC = employee?.user_type === 3;
    const isFinance = employee?.user_type === 11;
    const isCompleted = data_verif_owner && verif_owner_datetime;
    const canVerifyBC = isBC && !row.verif_bc_id && !isCompleted && today < new Date(row.expired_date);
    const canVerifyFinance = isFinance && row.verif_bc_id && !row.verif_owner_id && today < new Date(row.expired_date);
    const expired = today > new Date(row.expired_date);

    return (
      <div className="elevated-card p-4 col-start-start gap-3 w-full">
        {/* Header with status */}
        <div className="row-between-center w-full">
          <div className="row-start-center gap-2">
            <i className="pi pi-box text-xl text-indigo-600"></i>
            <span className="font-bold text-base">Request #{row.no}</span>
          </div>
          <div className={`${statusInfo.bgLight} ${statusInfo.textColor} px-3 py-1 rounded-full row-center-center gap-2`}>
            <i className={`pi ${statusInfo.icon} text-xs`}></i>
            <span className="text-xs font-semibold">{statusInfo.status}</span>
          </div>
        </div>

        {/* Customer Info */}
        <div className="col-start-start gap-1 w-full border-l-4 border-indigo-400 pl-3">
          <div className="row-start-center gap-2">
            <i className="pi pi-user text-indigo-600"></i>
            <span className="font-semibold text-sm text-gray-500">Customer</span>
          </div>
          <span className="font-semibold">{data_customer?.name} ({data_customer?.code})</span>
          <span className="text-sm text-gray-600">BC: {data_customer?.data_bc?.name}</span>
          {row?.sales_discount_id && (
            <span className="text-sm text-gray-600">Invoice: {data_sales_discount?.data_sales?.number}</span>
          )}
        </div>

        {/* Discount Info */}
        <div className="col-start-start gap-1 w-full border-l-4 border-purple-400 pl-3">
          <div className="row-start-center gap-2">
            <i className="pi pi-ticket text-purple-600"></i>
            <span className="font-semibold text-sm text-gray-500">Discount</span>
          </div>
          <span className="font-semibold">{row?.name}</span>
          <span className="text-sm text-gray-600">
            Value:{" "}
            {type === 1
              ? new Intl.NumberFormat("id-ID", {
                style: "currency",
                currency: "IDR",
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              }).format(value)
              : `${value}%`}
          </span>
          <span className="text-sm text-gray-600">Expired: {format(new Date(expired_date), "dd MMM yyyy")}</span>
          {row.description && <span className="text-sm text-gray-600">Description: {row.description}</span>}
        </div>

        {/* Timeline */}
        <div className="col-start-start gap-2 w-full">
          <div className="row-start-center gap-2">
            <i className="pi pi-history text-gray-600"></i>
            <span className="font-semibold text-sm text-gray-500">Timeline</span>
          </div>
          {/* Horizontal Timeline for Mobile */}
          <div className="row-center-center gap-0 w-full relative">
            {/* Created Step */}
            <div className="col-start-center gap-1 flex-1 relative min-w-0 h-[8rem]">
              <div className="w-10 h-10 rounded-full bg-green-600 row-center-center z-10 flex-shrink-0 absolute top-0">
                <i className="pi pi-user text-white text-sm"></i>
              </div>
              <div className="col-center-center gap-0.5 mt-12 w-full">
                <span className="text-xs font-semibold text-gray-700 text-center">Created</span>
                <span className="text-[13px] font-semibold text-center break-words w-full px-1 line-clamp-2">{data_created_by?.name}</span>
                <span className="text-[10px] font-semibold">{format(new Date(created_by_datetime), "dd/MM HH:mm")}</span>
              </div>
            </div>

            {/* Connector Line 1 */}
            <div className={`w-8 h-1 ${data_verif_bc || isCompleted ? 'bg-green-600' : 'bg-gray-300'} flex-shrink-0 absolute left-[calc(33.333%-1rem)] top-[1.25rem]`}></div>

            {/* BC Verified Step */}
            <div className="col-start-center gap-1 flex-1 relative min-w-0 h-[8rem]">
              <div
                className={`w-10 h-10 rounded-full flex-shrink-0 ${data_verif_bc ? 'bg-green-600' : canVerifyBC ? 'bg-blue-600 shadow-lg' : 'bg-gray-300'} row-center-center z-10 absolute top-0 ${canVerifyBC && 'cursor-pointer active:bg-blue-700 active:shadow-xl transition-all'}`}
                onClick={() => canVerifyBC && confirmVerifBC(row)}
              >
                {canVerifyBC ? (
                  <i className="pi pi-check text-white text-sm font-bold"></i>
                ) : (
                  <i className={`pi pi-check text-white text-sm ${!data_verif_bc && 'opacity-50'}`}></i>
                )}
              </div>
              <div className="col-center-center gap-0.5 mt-12 w-full">
                <span className={`text-xs font-semibold text-center ${data_verif_bc ? 'text-green-600' : canVerifyBC ? 'text-blue-700' : 'text-gray-400'}`}>
                  {canVerifyBC ? 'Tap to Verify' : 'BC Verified'}
                </span>
                {data_verif_bc ? (
                  <>
                    <span className="text-[13px] font-semibold text-center break-words w-full px-1 line-clamp-2">{data_verif_bc?.name}</span>
                    <span className="text-[10px] font-semibold">{format(new Date(verif_bc_datetime), "dd/MM HH:mm")}</span>
                  </>
                ) : canVerifyBC ? (
                  <span className="text-[10px] text-blue-700 text-center font-bold">Ready</span>
                ) : (
                  <span className="text-[10px] text-gray-400 text-center">
                    {expired
                      ? "Expired"
                      : data_verif_owner
                        ? "Verified by Finance"
                        : "Pending"}
                  </span>
                )}
              </div>
            </div>

            {/* Connector Line 2 */}
            <div className={`w-8 h-1 ${data_verif_owner ? 'bg-green-500' : 'bg-gray-300'} flex-shrink-0 absolute left-[calc(66.666%-1rem)] top-[1.25rem]`}></div>

            {/* Finance Verified Step */}
            <div className="col-start-center gap-1 flex-1 relative min-w-0 h-[8rem]">
              <div
                className={`w-10 h-10 rounded-full flex-shrink-0 ${data_verif_owner ? 'bg-green-600' : canVerifyFinance ? 'bg-green-600 shadow-lg' : 'bg-gray-300'} row-center-center z-10 absolute top-0 ${canVerifyFinance && 'cursor-pointer active:bg-green-700 active:shadow-xl transition-all'}`}
                onClick={() => canVerifyFinance && confirmVerifFinance(row)}
              >
                {canVerifyFinance ? (
                  <i className="pi pi-check-circle text-white text-sm font-bold"></i>
                ) : (
                  <i className={`pi pi-check-circle text-white text-sm ${!data_verif_owner && 'opacity-50'}`}></i>
                )}
              </div>
              <div className="col-center-center gap-0.5 mt-12 w-full">
                <span className={`text-xs font-semibold text-center ${data_verif_owner ? 'text-green-700' : canVerifyFinance ? 'text-green-700' : 'text-gray-400'}`}>
                  {canVerifyFinance ? 'Tap to Verify' : 'Finance Verified'}
                </span>
                {data_verif_owner ? (
                  <>
                    <span className="text-[13px] font-semibold text-center break-words w-full px-1 line-clamp-2">{data_verif_owner?.name}</span>
                    <span className="text-[10px] font-semibold">{format(new Date(verif_owner_datetime), "dd/MM HH:mm")}</span>
                  </>
                ) : canVerifyFinance ? (
                  <span className="text-[10px] text-green-700 text-center font-bold">Ready</span>
                ) : (
                  <span className="text-[10px] text-gray-400 text-center">
                    {expired
                      ? "Expired"
                      : "Pending"}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="row-center-center gap-2 w-full pt-2 border-t border-gray-200">
          <Button
            icon="pi pi-pencil"
            label="Edit"
            severity="primary"
            className="bg-indigo-500 flex-1"
            onClick={() => {
              if (onClickRowEdit) onClickRowEdit(row);
            }}
            disabled={row.verif_bc_id || row.verif_owner_id}
          />
          <Button
            icon="pi pi-trash"
            label="Delete"
            severity="danger"
            className="bg-red-500 flex-1"
            onClick={() => {
              if (onClickRowDelete) onClickRowDelete(row);
            }}
          />
        </div>
      </div>
    );
  };

  const isCasher = employee?.user_type === 8;
  const isSuperAdmin = employee?.user_type === 1;

  return (
    <div className="col-start-start w-full gap-4">
      {/* filter section */}
      <div className="elevated-card-tight-vertical row-start-start w-full gap-4">
        <CustomerDiscountFilter
          filterValue={filterValue}
          setFilterValue={setFilterValue}
          onClickRefresh={handleOnClickRefresh}
        />
      </div>

      {/* Add New Button */}
      {(isCasher || isSuperAdmin) && (
        <div className="elevated-card-tight-vertical col-start-end w-full gap-2 p-4">
          <div className="row-start-center w-full">
            <Button
              icon="pi pi-plus"
              label="New Request"
              onClick={() => {
                if (onClickAddNew) onClickAddNew();
              }}
              disabled={!isCasher && !isSuperAdmin}
            />
          </div>
        </div>
      )}

      {/* Pending Requests Section */}
      <div className="elevated-card-tight-vertical col-start-end w-full gap-2">
        <div className="row-start-center w-full gap-2 pb-2 border-b border-gray-200">
          <i className="pi pi-hourglass text-orange-600 text-xl"></i>
          <h3 className="text-lg font-bold text-orange-700">Pending Requests</h3>
          {listDataPending && listDataPending.length > 0 && (
            <span className="bg-orange-100 text-orange-700 px-2 py-1 rounded-full text-sm font-semibold">
              {listDataPending.length}
            </span>
          )}
        </div>

        {isMobile ? (
          // Mobile Card View - Pending
          <div className="col-start-start w-full gap-3">
            {loadingPending ? (
              <div className="col-center-center w-full py-8">
                <i className="pi pi-spin pi-spinner text-3xl text-orange-500"></i>
              </div>
            ) : listDataPending && listDataPending.length > 0 ? (
              listDataPending.map((row) => <MobileCardItem key={row.id} row={row} />)
            ) : (
              <div className="col-center-center w-full py-8 text-gray-500">
                <i className="pi pi-check-circle text-4xl mb-2 text-green-500"></i>
                <span>No pending requests</span>
              </div>
            )}
          </div>
        ) : (
          // Desktop Table View - Pending
          <DataTable
            dataKey="id"
            className="w-100"
            loading={loadingPending}
            showGridlines
            stripedRows
            value={listDataPending}
            emptyMessage="No pending requests"
          >
            <Column
              field="no"
              header="#"
              className="text-center"
              headerClassName="w-[4rem]"
              alignHeader={"center"}
            />
            <Column
              field=""
              header="Action"
              alignHeader={"center"}
              headerClassName="w-[8rem]"
              body={actionBodyTemplate}
            />
            <Column header='Customer' field='data_customer.name' body={CustomerBodyTemplate} headerClassName="w-[12rem]" />
            <Column header='Discount' field='name' body={DiscountBodyTemplate} headerClassName="w-[12rem]" />
            <Column header='Timeline' field='timeline' body={TimelineBodyTemplate} />
          </DataTable>
        )}
      </div>

      {/* Completed Requests Section */}
      <div className="elevated-card-tight-vertical col-start-center w-full gap-2">
        <div className="row-start-center w-full gap-2 pb-2 border-b border-gray-200">
          <i className="pi pi-check-circle text-green-600 text-xl"></i>
          <h3 className="text-lg font-bold text-green-700">Completed Requests</h3>
          {listData && listData.length > 0 && (
            <span className="bg-green-100 text-green-700 px-2 py-1 rounded-full text-sm font-semibold">
              {listData.length}
            </span>
          )}
        </div>

        {isMobile ? (
          // Mobile Card View - Completed
          <div className="col-start-start w-full gap-3">
            {loadingMain ? (
              <div className="col-center-center w-full py-8">
                <i className="pi pi-spin pi-spinner text-3xl text-green-500"></i>
              </div>
            ) : listData && listData.length > 0 ? (
              listData.map((row) => <MobileCardItem key={row.id} row={row} />)
            ) : (
              <div className="col-center-center w-full py-8 text-gray-500">
                <i className="pi pi-inbox text-4xl mb-2"></i>
                <span>No completed requests</span>
              </div>
            )}
          </div>
        ) : (
          // Desktop Table View - Completed
          <DataTable
            dataKey="id"
            className="w-100"
            loading={loadingMain}
            showGridlines
            stripedRows
            value={listData}
            emptyMessage="No completed requests"
          >
            <Column
              field="no"
              header="#"
              className="text-center"
              headerClassName="w-[4rem]"
              alignHeader={"center"}
            />
            <Column
              field=""
              header="Action"
              alignHeader={"center"}
              headerClassName="w-[10rem]"
              body={actionBodyTemplate}
            />
            <Column header='Customer' field='data_customer.name' body={CustomerBodyTemplate} headerClassName="w-[12rem]" />
            <Column header='Discount' field='name' body={DiscountBodyTemplate} headerClassName="w-[12rem]" />
            <Column header='Timeline' field='timeline' body={TimelineBodyTemplate} />
          </DataTable>
        )}
        {isMobile ? (
          <Paginator
            first={parseInt(paging.take) * (paging.page - 1)}
            rows={parseInt(paging.take)}
            totalRecords={parseInt(paging.total)}
            template={{ layout: "PrevPageLink CurrentPageReport NextPageLink" }}
            onPageChange={handleOnPage}
            pt={{
              prevPageButton: {
                className:
                  "bg-red-100 !m-0 !max-w-[22px] !w-[22px] !min-w-[22px] max-h-[22px]",
              },
              nextPageButton: {
                className:
                  "bg-red-100 !m-0 !max-w-[22px] !w-[22px] !min-w-[22px] max-h-[22px]",
              },
              current: {
                className: "bg-red-100 !m-0 max-h-[22px] text-sm",
              },
            }}
          />
        ) : (
          <Paginator
            first={parseInt(paging.take) * (paging.page - 1)}
            rows={parseInt(paging.take)}
            totalRecords={parseInt(paging.total)}
            rowsPerPageOptions={[5, 10, 20, 50]}
            onPageChange={handleOnPage}
          />
        )}
      </div>

      {/* Expired Requests Section */}
      <div className="elevated-card-tight-vertical col-start-center w-full gap-2">
        <div className="row-start-center w-full gap-2 pb-2 border-b border-gray-200">
          <i className="pi pi-times-circle text-red-600 text-xl"></i>
          <h3 className="text-lg font-bold text-red-700">Expired Requests</h3>
          {listDataExpired && listDataExpired.length > 0 && (
            <span className="bg-red-100 text-red-700 px-2 py-1 rounded-full text-sm font-semibold">
              {listDataExpired.length}
            </span>
          )}
        </div>

        {isMobile ? (
          // Mobile Card View - Completed
          <div className="col-start-start w-full gap-3">
            {loadingExpired ? (
              <div className="col-center-center w-full py-8">
                <i className="pi pi-spin pi-spinner text-3xl text-green-500"></i>
              </div>
            ) : listDataExpired && listDataExpired.length > 0 ? (
              listDataExpired.map((row) => <MobileCardItem key={row.id} row={row} />)
            ) : (
              <div className="col-center-center w-full py-8 text-gray-500">
                <i className="pi pi-inbox text-4xl mb-2"></i>
                <span>No Expired requests</span>
              </div>
            )}
          </div>
        ) : (
          // Desktop Table View - Completed
          <DataTable
            dataKey="id"
            className="w-100"
            loading={loadingExpired}
            showGridlines
            stripedRows
            value={listDataExpired}
            emptyMessage="No Expired requests"
          >
            <Column
              field="no"
              header="#"
              className="text-center"
              headerClassName="w-[4rem]"
              alignHeader={"center"}
            />
            <Column
              field=""
              header="Action"
              alignHeader={"center"}
              headerClassName="w-[10rem]"
              body={actionBodyTemplate}
            />
            <Column header='Customer' field='data_customer.name' body={CustomerBodyTemplate} headerClassName="w-[12rem]" />
            <Column header='Discount' field='name' body={DiscountBodyTemplate} headerClassName="w-[12rem]" />
            <Column header='Timeline' field='timeline' body={TimelineBodyTemplate} />
          </DataTable>
        )}
        {isMobile ? (
          <Paginator
            first={parseInt(paging.take) * (paging.page - 1)}
            rows={parseInt(paging.take)}
            totalRecords={parseInt(paging.total)}
            template={{ layout: "PrevPageLink CurrentPageReport NextPageLink" }}
            onPageChange={handleOnPage}
            pt={{
              prevPageButton: {
                className:
                  "bg-red-100 !m-0 !max-w-[22px] !w-[22px] !min-w-[22px] max-h-[22px]",
              },
              nextPageButton: {
                className:
                  "bg-red-100 !m-0 !max-w-[22px] !w-[22px] !min-w-[22px] max-h-[22px]",
              },
              current: {
                className: "bg-red-100 !m-0 max-h-[22px] text-sm",
              },
            }}
          />
        ) : (
          <Paginator
            first={parseInt(paging.take) * (paging.page - 1)}
            rows={parseInt(paging.take)}
            totalRecords={parseInt(paging.total)}
            rowsPerPageOptions={[5, 10, 20, 50]}
            onPageChange={handleOnPage}
          />
        )}
      </div>

      {/* Confirmation Dialog */}
      <ConfirmDialog />
    </div>
  );
});

export default CustomerDiscountList;
